import React from 'react';
import { AlertCircleIcon, ChevronDownIcon } from 'lucide-react';
export interface FieldOption {
  value: string;
  label: string;
}
interface FormFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  as?: 'input' | 'select' | 'textarea';
  type?: string;
  options?: FieldOption[];
  placeholder?: string;
  hint?: string;
  autoComplete?: string;
  rows?: number;
  maxLength?: number;
  className?: string;
}
export function FormField({
  id,
  label,
  value,
  onChange,
  error,
  required = false,
  as = 'input',
  type = 'text',
  options = [],
  placeholder,
  hint,
  autoComplete,
  rows = 5,
  maxLength,
  className = ''
}: FormFieldProps) {
  const describedBy = [hint ? `${id}-hint` : '', error ? `${id}-error` : ''].filter(Boolean).join(' ') || undefined;
  const control = `mt-2 block w-full rounded-lg border bg-white px-4 py-3 text-[15px] text-plum placeholder:text-plum/40 transition-[border-color,box-shadow] duration-150 focus:border-copper focus:outline-none focus:ring-2 focus:ring-copper/25 ${error ? 'border-danger' : 'border-plum/15 hover:border-plum/30'}`;
  const shared = {
    id,
    name: id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
    'aria-required': required || undefined
  };
  return <div className={className} data-id="element-143" data-mp="ulk38lid3y2n">
      <label htmlFor={id} className="block text-sm font-medium text-plum" data-id="element-144" data-mp="3u3uluvdyb0qe">
        {label}
        {required ? <span className="ml-0.5 text-copper-deep" aria-hidden="true" data-id="element-145" data-mp="20253zmom0i6">
            *
          </span> : <span className="ml-1.5 font-normal text-plum/50" data-id="element-146" data-mp="6wsgwarge9xs">(optional)</span>}
      </label>
      {hint && <p id={`${id}-hint`} className="mt-1 text-xs text-plum/60" data-id="element-147" data-mp="br5671fm2fhg">
          {hint}
        </p>}
      {as === 'select' ? <div className="relative" data-id="element-148" data-mp="3fdncd55x8s1u">
          <select {...shared} value={value} onChange={e => onChange(e.target.value)} className={`${control} appearance-none pr-10`} data-id="element-149" data-mp="3fcubgktn8wrm">
            <option value="" data-id="element-150" data-mp="mhc3mtdw4v3p">Select…</option>
            {options.map(o => <option key={o.value} value={o.value} data-id="element-151" data-mp="hkbx6hd4pqc2">
                {o.label}
              </option>)}
          </select>
          <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-1/2 mt-1 h-4 w-4 -translate-y-1/2 text-plum/50" aria-hidden="true" data-id="element-152" data-mp="rancf029jly9" />
        </div> : as === 'textarea' ? <textarea {...shared} value={value} rows={rows} maxLength={maxLength} placeholder={placeholder} onChange={e => onChange(e.target.value)} className={`${control} resize-y leading-relaxed`} data-id="element-153" data-mp="3ur3xi5rnqi14" /> : <input {...shared} type={type} value={value} maxLength={maxLength} placeholder={placeholder} autoComplete={autoComplete} onChange={e => onChange(e.target.value)} className={control} data-id="element-154" data-mp="7oqwqnnpky4f" />}
      {error && <p id={`${id}-error`} className="mt-2 flex items-start gap-1.5 text-sm text-danger" data-id="element-155" data-mp="1vk6v40dezg2x">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-156" data-mp="1hke03suuudp5" />
          {error}
        </p>}
    </div>;
}